"use client";

import { motion } from "framer-motion";
import { FastForward, CandlestickChart } from "lucide-react";
import { getViewModeChrome } from "../view-mode-chrome";

interface FastForwardLoadingProps {
  progress?: number; // 0-1
  state: 'loading' | 'streaming';
  viewMode?: string;
}

export function FastForwardLoading({ progress = 0, state, viewMode }: FastForwardLoadingProps) {
  const chrome = getViewModeChrome(viewMode);
  const isStreaming = state === 'streaming';

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ 
        opacity: 1, 
        y: 0
      }}
      transition={{ duration: 0.2 }}
      className="w-full p-1"
    >
      <div className={`-mt-2 relative overflow-hidden rounded-md border ${chrome.card} ${
        isStreaming ? chrome.streamingBorder : ""
      }`}>
        {/* Gradient shimmer effect for streaming */}
        {isStreaming && (
          <motion.div
            className="absolute inset-0 rounded-md"
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)"
            }}
            animate={{
              x: ["-100%", "100%"]
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        )}

        {/* Progress bar for loading state only */}
        {!isStreaming && (
          <div className="absolute top-0 left-0 h-[1px] bg-gradient-to-r from-transparent via-border to-transparent">
            <motion.div
              className={`h-full bg-gradient-to-r ${
                chrome.isSignal
                  ? 'from-emerald-400/0 via-emerald-400/80 to-emerald-400/0'
                  : chrome.isAlert
                    ? 'from-sky-400/0 via-sky-400/80 to-sky-400/0'
                    : 'from-green-500/0 via-green-500/80 to-green-500/0'
              }`}
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ 
                duration: 2, 
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
          </div>
        )}

        <div className="px-3 py-2 relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <CandlestickChart
                  className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`}
                  strokeWidth={2}
                />
              <div className="flex flex-col">
                <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                  {state === 'loading' ? 'Fetching price data...' : 'Replaying price action...'}
                </span>
                <span className={`text-[11px] ${chrome.subtext} truncate`}>
                  {state === 'loading' 
                    ? 'Loading historical data' 
                    : 'Streaming live price updates'
                  }
                </span>
              </div>
            </div>
            <FastForward className={`h-5 w-5 ${chrome.iconMuted} animate-pulse`}
                         style={{ animationDuration: '2s' }} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
